/**
 * 모든 튜닝 파라미터의 단일 출처(single source of truth).
 * 룸 크기·속도·파티클 수·색상 등은 코드에 흩뿌리지 말고 여기서만 바꾼다.
 */

/** 룸 치수 (m). BackSide 박스의 내부 공간. */
export const ROOM = { w: 16, h: 6, d: 16 } as const;

/** 데스크탑 기준 눈높이 (m). VR에선 헤드셋이 바닥 기준 높이를 제공한다. */
export const EYE = 1.6;

export const CAMERA = { fov: 72, near: 0.1, far: 100 } as const;

export const FOG = { color: 0x0a0710, density: 0.018 } as const;

export const RENDER = {
  /** devicePixelRatio 상한 — 모바일 GPU 과부하 방지 (Quest 성능 예산). */
  maxPixelRatio: 2,
} as const;

export const LOOP = {
  /** 프레임 간 dt 상한 (초). 탭 비활성 복귀 시 큰 점프 방지. */
  maxDelta: 0.05,
} as const;

export const WALL = {
  /** 벽 셰이더 팔레트 (선형 RGB). r128 룩 보존용 실측값 → 셰이더에 GLSL 리터럴로 주입된다. */
  palette: {
    deep: [0.020, 0.014, 0.030],
    amber: [0.62, 0.30, 0.09],
    gold: [0.95, 0.74, 0.30],
  },
  /** fbm 노이즈 흐름 속도. */
  timeScale: 0.045,
} as const;

export const PARTICLES = {
  count: 1400,
  size: 0.045,
  color: 0xf0c060,
  opacity: 0.8,
  /** 상승 속도 = minVel + random()*velRange (m/s). */
  minVel: 0.15,
  velRange: 0.35,
  /** 천장에 닿으면 이 높이로 리셋. */
  resetY: 0.05,
  /** 스폰 시 벽에서 띄울 여백 (m). */
  spawnMargin: 1,
} as const;

export const DESKTOP = {
  sensitivity: 0.0022,
  speed: 3.2, // m/s
  pitchClamp: 1.4, // rad — 위아래 시선 제한
  wallMargin: 0.7, // 벽 충돌 여백 (m)
} as const;

export const XR = {
  /** 좌/우 컨트롤러. */
  controllerCount: 2,
  optionalFeatures: ['local-floor', 'bounded-floor', 'hand-tracking', 'layers'],
  labels: {
    checking: 'VR 확인 중…',
    enter: 'VR로 입장',
    exit: 'VR 종료',
    unsupported: '이 기기 VR 미지원',
    noWebXR: 'WebXR 미지원',
    needsHttps: 'VR엔 HTTPS 필요',
    checkFailed: 'VR 확인 불가',
    enterFailed: 'VR 입장 실패',
  },
} as const;

/**
 * 벽에 띄울 퍼블릭 도메인 명화 (사후 70년 경과작, 전부 Wikimedia Public Domain).
 * particleColor = 그 명화가 나타날 때 입자가 물드는 별빛/광휘 색.
 * 배열 순서 = 챕터 순서 = 피날레에서 각 면에 배정되는 순서.
 */
export const PAINTINGS = [
  { id: 'starry-night', path: 'paintings/starry-night.jpg', title: '별이 빛나는 밤', artist: 'Vincent van Gogh', particleColor: 0xfff2cc },
  { id: 'klimt-kiss', path: 'paintings/klimt-kiss.jpg', title: '키스', artist: 'Gustav Klimt', particleColor: 0xffdf8a },
  { id: 'hokusai-wave', path: 'paintings/hokusai-wave.jpg', title: '가나가와 해변의 큰 파도', artist: 'Katsushika Hokusai', particleColor: 0xcfe6ff },
  { id: 'monet-sunrise', path: 'paintings/monet-sunrise.jpg', title: '인상, 해돋이', artist: 'Claude Monet', particleColor: 0xffcf9c },
  { id: 'vangogh-cafe', path: 'paintings/vangogh-cafe.jpg', title: '밤의 카페테라스', artist: 'Vincent van Gogh', particleColor: 0xffe39a },
  { id: 'vermeer-pearl', path: 'paintings/vermeer-pearl.jpg', title: '진주 귀걸이를 한 소녀', artist: 'Johannes Vermeer', particleColor: 0xf2e4c4 },
] as const;

/**
 * 쇼 타임라인 (초). 인트로 → 6챕터(명화 1점씩 피어남·머묾·용해) →
 * 피날레(6면에 6명화 동시 + 다채 입자) → 서서히 암전 → 정전 휴식 → 처음부터 반복.
 */
export const SHOW = {
  introCalm: 6, // 시작 고요 (황금 추상)
  chapter: { bloom: 7, life: 8, release: 6 }, // 챕터당 비트 (서서히 피어나게 길게)
  betweenChapters: 2, // 챕터 사이 짧은 고요
  finale: { bloom: 8, hold: 10 }, // 6면 동시 피어남 + 머묾
  fadeOut: 8, // 서서히 암전
  darkRest: 10, // 정전 휴식 후 처음부터
} as const;

/** 황금 추상 ↔ 명화 모핑 파라미터. */
export const MORPH = {
  softness: 0.25, // 노이즈 마스크 dissolve 경계 폭 (클수록 더 부드럽게 번지며 나타남)
  uvRepeat: 1, // 면당 명화 반복 수. 1 = 한 면에 한 장 꽉.
} as const;

/** 피날레 전용 다채 입자 팔레트 — 챕터 별빛보다 채도를 높여 '다채롭게'. */
export const FINALE = {
  particlePalette: [0xffc24a, 0x5aa0ff, 0x40d0c0, 0xff8a4a, 0xc888ff, 0xff6a9a],
} as const;

/** 배경음악 (ACE-Step 생성). 사용자 제스처에서 재생 시작, 끝(암전)에서 게인 페이드아웃. */
export const AUDIO = {
  path: 'audio/lumina-bgm.mp3',
  volume: 0.55, // 마스터 볼륨 (BGM이 과하지 않게)
} as const;
